import { inject } from '@angular/core';
import { NotificationService } from '@iot-platform/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { concatMap, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { UserApiKeysActions } from '../actions';
import { UserApiKeysService } from '../../services/user-api-keys.service';
import { UserApiKey } from '@iot-platform/models/i4b';

const loadUserApiKeys$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.loadUserApiKeys),
      concatMap(({ userId }) =>
        userApiKeysService.getUserApiKeys(userId).pipe(
          map((response: UserApiKey[]) => UserApiKeysActions.loadUserApiKeysSuccess({ response })),
          catchError((error) => of(UserApiKeysActions.loadUserApiKeysFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const createUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.createUserApiKey),
      concatMap(({ userId, userApiKey }) =>
        userApiKeysService.createUserApiKey(userId, userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.createUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.createUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const editUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.editUserApiKey),
      concatMap(({ userApiKey }) =>
        userApiKeysService.updateUserApiKey(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.editUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.editUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const enableUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.enableUserApiKey),
      concatMap(({ userApiKey }) =>
        userApiKeysService.updateUserApiKey(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.enableUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.enableUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const disableUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.disableUserApiKey),
      concatMap(({ userApiKey }) =>
        userApiKeysService.updateUserApiKey(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.disableUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.disableUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const renewUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.renewUserApiKey),
      concatMap(({ userApiKey }) =>
        userApiKeysService.renewUserApiKey(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.renewUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.renewUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const manageUserApiKeyUsagePlan$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.manageUserApiKeyUsagePlan),
      concatMap(({ userApiKey }) =>
        userApiKeysService.updateUserApiKeyUsagePlan(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.manageUserApiKeyUsagePlanSuccess({ response })),
          catchError((error) => of(UserApiKeysActions.manageUserApiKeyUsagePlanFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const deleteUserApiKey$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), userApiKeysService = inject(UserApiKeysService)) =>
    actions$.pipe(
      ofType(UserApiKeysActions.deleteUserApiKey),
      concatMap(({ userApiKey }) =>
        userApiKeysService.deleteUserApiKey(userApiKey).pipe(
          map((response: UserApiKey) => UserApiKeysActions.deleteUserApiKeySuccess({ response })),
          catchError((error) => of(UserApiKeysActions.deleteUserApiKeyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const showLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        UserApiKeysActions.loadUserApiKeys,
        UserApiKeysActions.createUserApiKey,
        UserApiKeysActions.editUserApiKey,
        UserApiKeysActions.enableUserApiKey,
        UserApiKeysActions.disableUserApiKey,
        UserApiKeysActions.renewUserApiKey,
        UserApiKeysActions.manageUserApiKeyUsagePlan,
        UserApiKeysActions.deleteUserApiKey
      ),
      tap(() => notificationService.showLoader())
    ),
  { functional: true, dispatch: false }
);

const hideLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        UserApiKeysActions.loadUserApiKeysSuccess,
        UserApiKeysActions.loadUserApiKeysFailure,
        UserApiKeysActions.createUserApiKeySuccess,
        UserApiKeysActions.createUserApiKeyFailure,
        UserApiKeysActions.editUserApiKeySuccess,
        UserApiKeysActions.editUserApiKeyFailure,
        UserApiKeysActions.enableUserApiKeySuccess,
        UserApiKeysActions.enableUserApiKeyFailure,
        UserApiKeysActions.disableUserApiKeySuccess,
        UserApiKeysActions.disableUserApiKeyFailure,
        UserApiKeysActions.renewUserApiKeySuccess,
        UserApiKeysActions.renewUserApiKeyFailure,
        UserApiKeysActions.manageUserApiKeyUsagePlanSuccess,
        UserApiKeysActions.manageUserApiKeyUsagePlanFailure,
        UserApiKeysActions.deleteUserApiKeySuccess,
        UserApiKeysActions.deleteUserApiKeyFailure
      ),
      tap(() => notificationService.hideLoader())
    ),
  { functional: true, dispatch: false }
);

const displayError$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        UserApiKeysActions.loadUserApiKeysFailure,
        UserApiKeysActions.createUserApiKeyFailure,
        UserApiKeysActions.editUserApiKeyFailure,
        UserApiKeysActions.enableUserApiKeyFailure,
        UserApiKeysActions.disableUserApiKeyFailure,
        UserApiKeysActions.renewUserApiKeyFailure,
        UserApiKeysActions.manageUserApiKeyUsagePlanFailure,
        UserApiKeysActions.deleteUserApiKeyFailure
      ),
      tap((action: Action) => notificationService.displayError(action))
    ),
  { functional: true, dispatch: false }
);

const displaySuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        UserApiKeysActions.createUserApiKeySuccess,
        UserApiKeysActions.editUserApiKeySuccess,
        UserApiKeysActions.enableUserApiKeySuccess,
        UserApiKeysActions.disableUserApiKeySuccess,
        UserApiKeysActions.renewUserApiKeySuccess,
        UserApiKeysActions.manageUserApiKeyUsagePlanSuccess,
        UserApiKeysActions.deleteUserApiKeySuccess
      ),
      tap((action: Action) => notificationService.displaySuccess(action.type))
    ),
  { functional: true, dispatch: false }
);

export const UserApiKeysEffects = {
  loadUserApiKeys$,
  createUserApiKey$,
  editUserApiKey$,
  enableUserApiKey$,
  disableUserApiKey$,
  renewUserApiKey$,
  manageUserApiKeyUsagePlan$,
  deleteUserApiKey$,
  showLoader$,
  hideLoader$,
  displayError$,
  displaySuccess$
};
